/**
 * The "Instructions" of one asset: a short, editable text that says what the director makes. The
 * Studio field opens with the default text of the asset type in the theme and the style of the song.
 * The user can change it. The director follows it inside the rules of the theme: the colours, the
 * fonts, the texture and the text animation of the theme always apply, and the plan check enforces
 * them.
 *
 * The web app and music24-api build the default text with the same function, so the field shows
 * exactly the text that the server would use. The default comes from the theme table
 * (`releaseThemes.ts`), the styles (`assetStyles.ts`) and the asset types (`studioThemes.ts`).
 *
 * The text is English, like the director brief. It is data for the director, not product copy.
 */
import { resolveAssetStyle, type AssetStyleId, type WaveformShape } from './assetStyles';
import { RELEASE_THEMES, type ReleaseThemeBrief, type ReleaseThemeId, type ThemeTextAnimation } from './releaseThemes';
import { STUDIO_THEMES, type StudioMoment, type StudioThemeId } from './studioThemes';

/** The longest text that the server takes and stores. */
export const ASSET_INSTRUCTIONS_MAX_LENGTH = 600;

/** What the default text reads. Every field matches a Studio choice of the asset. */
export interface AssetInstructionsInput {
  /** The asset type (the code name `StudioThemeId`). */
  assetTypeId: StudioThemeId;
  /** The release theme of the song. */
  releaseThemeId: ReleaseThemeId;
  /** The style of the asset. Absent or null: the default style of the theme for the asset type. */
  style?: AssetStyleId | null;
  /** The waveform shape of the Waveform style or of the AI reel. Absent: the shape of the theme. */
  waveform?: WaveformShape | null;
  /** The moment of a story, a feed post or an artwork in motion. */
  moment?: StudioMoment;
  /**
   * True when the song has a synced lyric timing (`lyricTiming.status === 'synced'`). Only the music
   * video reads it: its captions are the synced lines. Absent: false.
   */
  lyricsSynced?: boolean;
}

/** How the text of each theme comes in, in words. */
const TEXT_ENTRANCE: Readonly<Record<ThemeTextAnimation, string>> = {
  fade: 'fades in',
  rise: 'rises in',
  slam: 'slams in',
  blurIn: 'comes into focus',
  typewriter: 'types itself in',
  wiggle: 'wiggles in',
};

const CUT_ON: Readonly<Record<ReleaseThemeBrief['cutOn'], string>> = {
  beat: 'on the beat',
  downbeat: 'on the first beat of each bar',
  phrase: 'at the end of each phrase',
};

const MOMENT_TEXT: Readonly<Record<StudioMoment, string>> = {
  preSave: '"Pre-save now"',
  outNow: '"Out now"',
  announce: 'the release date',
  lyricLine: 'your lyric line',
};

const WAVEFORM_TEXT: Readonly<Record<WaveformShape, string>> = {
  bars: 'bars that move',
  line: 'a line that moves',
  ring: 'a ring that moves',
  none: 'no waveform',
};

function pace(brief: ReleaseThemeBrief): string {
  const [shortest, longest] = brief.paceSecPerCut;
  return `a new shot every ${shortest} to ${longest} s, cut ${CUT_ON[brief.cutOn]}`;
}

/** The first sentence: the picture of the style. */
function pictureSentence(
  assetTypeId: StudioThemeId,
  style: AssetStyleId | null,
  waveform: WaveformShape | null,
  brief: ReleaseThemeBrief
): string {
  if (assetTypeId === 'aiReel') {
    const shape = waveform === null || waveform === 'none' ? '' : `, with ${WAVEFORM_TEXT[waveform]} with the song`;
    return `AI footage with a ${brief.footage.moods.join(' or ')} mood, ${pace(brief)}${shape}.`;
  }
  switch (style) {
    case null:
      return 'The cover at every size that the stores ask for, with no change.';
    case 'coverCard':
      return 'The cover sits as a card on the ground of the theme.';
    case 'coverGround':
      return 'The cover is blurred behind large type.';
    case 'typeOnly':
      return 'Big type on the ground of the theme, with no cover.';
    case 'drift':
      return 'A slow drift over the cover.';
    case 'pulse':
      return 'The cover pulses on the beat, with bars that move with the song.';
    case 'liquid':
      return 'The colours of the theme flow like ink over the cover.';
    case 'waveform':
      return `The cover, with ${WAVEFORM_TEXT[waveform ?? brief.waveform]} with the song.`;
    case 'particles':
      return 'Dots in the accent colour rise over the cover with the song.';
    case 'aiFootage':
      return `AI footage with a ${brief.footage.moods.join(' or ')} mood, ${pace(brief)}.`;
  }
}

/** The second sentence: the words on screen and how the asset ends. */
function textSentence(assetTypeId: StudioThemeId, moment: string, entrance: string, lyricsSynced: boolean): string {
  switch (assetTypeId) {
    case 'story':
      return `The title, the artist and ${moment} in the middle. The top and the bottom stay clear for the app.`;
    case 'feedPost':
      return `The title and ${moment} take at most a third of the frame.`;
    case 'banners':
      return 'The title and the artist sit in the centre band, so no site crops them.';
    case 'spotifyHeader':
      return '';
    case 'thumbnail':
      return 'The title in large type that reads at a small size.';
    case 'coverSizes':
      return '';
    case 'lyricCard':
      return 'Your lyric line is the hero, with the artist and the title small below it.';
    case 'linkPreview':
      return 'The title and the artist, clear at a small size.';
    case 'aiReel':
      return `The title ${entrance} in the first two seconds, the lyrics come in as captions, and it ends on the cover.`;
    case 'artworkInMotion':
      return `The title ${entrance} on the second beat with ${moment}, and it ends on the cover.`;
    case 'countdown':
      return `The days left ${entrance} on the beat, and it ends on the title.`;
    case 'canvasLoop':
      return 'No text and no logo. It loops with no jump, and the bottom third stays calm.';
    case 'lyricMoment':
      return `Each word of your lines ${entrance} at its sung time.`;
    case 'motionArtwork':
      return 'A slow, silent loop with no text.';
    case 'lyricVideo':
      return `Each line ${entrance} at its sung time.`;
    case 'visualizer':
      return `The title ${entrance} at the start, and the picture follows the whole song.`;
    case 'musicVideo':
      return lyricsSynced
        ? `The title ${entrance} at the start, and the footage follows the whole song, with your lyrics as captions.`
        : `The title ${entrance} at the start, and the footage follows the whole song.`;
  }
}

/**
 * The default instructions of an asset: one or two short sentences about what the director makes
 * with the style, the theme and the moment of the asset. Never empty. Pure.
 */
export function defaultAssetInstructions(input: AssetInstructionsInput): string {
  const { assetTypeId, releaseThemeId } = input;
  const { brief } = RELEASE_THEMES[releaseThemeId];
  const resolved = resolveAssetStyle(releaseThemeId, assetTypeId, {
    style: input.style ?? undefined,
    waveform: input.waveform ?? undefined,
  });
  const moment = MOMENT_TEXT[input.moment ?? 'outNow'];
  const parts = [
    pictureSentence(assetTypeId, resolved?.style ?? null, resolved?.waveform ?? null, brief),
    textSentence(assetTypeId, moment, TEXT_ENTRANCE[brief.textAnimation], input.lyricsSynced === true),
  ];
  return parts.filter((part) => part !== '').join(' ');
}

/**
 * True when the director reads the Instructions of an asset, so the Studio shows the field and the
 * server keeps an edited text. False for an image (owner decision 2026-10-08) and for a lyric video
 * in a style that code lays out (Cover ground, Cover card, Type only; design `asset-redesign/y3`):
 * code places every item of these, so a text could not change the file. A lyric video in the AI
 * footage style reads them (the director picks the clips). An absent style is the default style of
 * the theme. Pure.
 */
export function assetUsesInstructions(input: {
  assetTypeId: StudioThemeId;
  releaseThemeId: ReleaseThemeId;
  style?: unknown;
}): boolean {
  if (STUDIO_THEMES[input.assetTypeId].format === 'image') {
    return false;
  }
  if (input.assetTypeId !== 'lyricVideo') {
    return true;
  }
  return resolveAssetStyle(input.releaseThemeId, input.assetTypeId, { style: input.style })?.style === 'aiFootage';
}

/**
 * The text as the server takes it: Windows line ends become `\n`, control characters (except a
 * line end and a tab) go, the ends are trimmed and the text is cut to
 * `ASSET_INSTRUCTIONS_MAX_LENGTH`. Pure.
 */
export function normaliseAssetInstructions(text: string): string {
  return (
    text
      .replace(/\r\n?/g, '\n')
      // eslint-disable-next-line no-control-regex -- the control characters are what this removes
      .replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '')
      .trim()
      .slice(0, ASSET_INSTRUCTIONS_MAX_LENGTH)
      .trim()
  );
}

/**
 * The text that an asset stores: the normalised text when it differs from the default of the
 * asset, else null (an empty text, or the default). A stored null means "the default", so a new
 * version or a remake in another theme gets the default of that theme. Pure.
 */
export function customAssetInstructions(text: string | null | undefined, input: AssetInstructionsInput): string | null {
  if (text === null || text === undefined) {
    return null;
  }
  const normalised = normaliseAssetInstructions(text);
  if (normalised === '' || normalised === normaliseAssetInstructions(defaultAssetInstructions(input))) {
    return null;
  }
  return normalised;
}
